import React from 'react';
import ReactDOMServer from 'react-dom/server';
import { ALLOWED_DOMAINS } from './AllowedDomains';
import Link from '@amzn/meridian/link';
import { decode } from 'html-entities';
import DOMPurify from 'dompurify';

const anchorTagPattern = new RegExp(/<a[\s]+?[\s\S]*?<\/a>/);

// https://sage.amazon.com/posts/63948
const contactIdPattern = new RegExp('^A[A-Z0-9]{11,13}(?:(?![A-Z0-9]))*');

// https://tiny.amazon.com/dcmu68j2/AbuseActions/release/RegExValidator.java
const retrailOrderIdPattern = new RegExp(
  '([SsCcd]d{2}|d[Cc]d|d{2}[Cc])-d{7}-d{7}'
);
const digitalOrderIdPattern = new RegExp('[Dd]d{2}-d{7}-d{7}');
const paymentOrderIdPattern = new RegExp('([Pp]d{2}|[Pp]w{1}d{1})-d{7}-d{7}');

// not include "@/-" which may exist for alias, orderId or issue code
const specialCharactersPattern = new RegExp(
  /[`!#$%^&*()_+=[\]{};':"\\|,.<>?~]/,
  'g'
);

// https://stackoverflow.com/questions/6038061/regular-expression-to-find-urls-within-a-string
const urlPattern = new RegExp(
  /(?:http|ftp|https):\/\/(?:[\w_-]+(?:(?:\.[\w_-]+)+))(?:[\w.,@?^=%&:/~+#;-]*[\w@?^=%&/~+#;-])?/
);
const anchorOrUrlPattern = new RegExp(
  '(' + anchorTagPattern.source + '|' + urlPattern.source + ')'
);

const emailAddressRegex = '[A-Z0-9._%+-]+@[A-Z0-9.-]+\\.[A-Z]{2,}'; //https://www.regular-expressions.info/email.html
const emailAddressRestrictedRegex = 'RESTRICTED:EMAIL_ADDRESS';
const emailAddressMayOrMayNotBeRestrictedRegex =
  '(' + emailAddressRegex + '|' + emailAddressRestrictedRegex + ')';
const emailAddressEnclosingHTMLTagsRegex =
  '<' + emailAddressMayOrMayNotBeRestrictedRegex + '>';
const pattern = new RegExp(emailAddressEnclosingHTMLTagsRegex, 'ig'); //case-insensitive + global matches

const AMAZON_INTERNAL_DOMAIN_PREFIXES = [
  'amazon',
  'imdb',
  'zappos',
  'audible',
  'a9',
  'lab126',
  'a2z',
  'alexa',
];
const emailAddressDomainRegex = '[A-Z0-9._%+-]+@([A-Z0-9-]+).';
const emailAddressDomainPattern = new RegExp(emailAddressDomainRegex, 'i');

// Updated version of the pattern in use at CSPhoenixGW
// https://tiny.amazon.com/15fhvflv9/codeamazpackBootblobmainsrc
const hmdSurveyLinkPattern = new RegExp(
  '((?:http|https)://(?:(?:' +
    'survey\\.woot\\.com/\\?p' +
    '|www\\.amazon\\..*?/gp/help/survey\\?p' +
    '|www\\.acx\\.com/HMDThankYou\\?c' +
    '|www\\.audible\\.com/(?:HMDSurvey|HMDThankYou)\\?(?:p|c)' +
    '|.*?survey\\?p' +
    ')(?:=|&#61;)))(\\w*)',
  'g'
);

export function processHtml(text: string): string {
  let processedText = filterHmdLinks(text);
  processedText = convertUrlToAnchorTag(processedText);
  processedText = retainEmailAddressEnclosedByHTMLTags(processedText);
  processedText = DOMPurify.sanitize(processedText);
  return processedText;
}

function filterHmdLinks(text: string): string {
  return text.replace(
    hmdSurveyLinkPattern,
    (_, urlPrefix) => `${urlPrefix}*************`
  );
}

function convertUrlToAnchorTag(text: string): string {
  // This pattern is similar to the URL regex above, but with a capture group on the domain.
  const re =
    /(?:http|ftp|https):\/\/([\w_-]+(?:(?:\.[\w_-]+)+))(?:[\w.,@?^=%&:/~+#;-]*[\w@?^=%&/~+#;-])?/g;

  let processedText = '';
  // Our splitter regex needs to be wrapped in capturing parentheses () so that matched results are included in the array.
  const textChunks = text.split(
    new RegExp('(' + anchorTagPattern.source + ')')
  );
  for (const textChunk of textChunks) {
    if (anchorTagPattern.test(textChunk)) {
      // Modify tag to open in a new tab
      processedText += convertAnchorTabToOpenInNewTab(textChunk);
    } else {
      processedText += textChunk.replace(re, (match, domain) => {
        if (ALLOWED_DOMAINS.has(domain)) {
          const url = decode(match);
          return ReactDOMServer.renderToStaticMarkup(createAnchorTag(url, url));
        } else {
          return match;
        }
      });
    }
  }
  return processedText;
}

function convertAnchorTabToOpenInNewTab(text: string): string {
  // Capture domain, url and text from anchor tag
  // group 1 is url
  // group 2 is domain
  // group 3 is text
  const re =
    /<a[\s]+?[\s\S]*?href="((?:http|ftp|https):\/\/([\w_-]+(?:(?:\.[\w_-]+)+))(?:[\w.,@?^=%&:/~+#;-]*[\w@?^=%&/~+#;-])?)"(?:[^<]+)>([^<]+)<\/a>/g;
  const newUrl = text.replace(re, (match, url, domain, text) => {
    if (ALLOWED_DOMAINS.has(domain)) {
      return ReactDOMServer.renderToStaticMarkup(
        createAnchorTag(decode(url), text)
      );
    } else {
      return match;
    }
  });
  return newUrl;
}

function createAnchorTag(url: string, text: string): JSX.Element {
  // Truncate text at 80 characters. https://tiny.amazon.com/1b2pf01wr/convert-text-links-t
  const anchorTagText = text.length < 80 ? text : text.slice(0, 80) + '...';
  // Using target="_blank" without rel="noopener noreferrer" is a security risk: see https://mathiasbynens.github.io/rel-noopener
  return (
    <Link
      href={url}
      target='_blank'
      rel='noopener noreferrer'
      key={Math.random().toString(36)}
    >
      {anchorTagText}
    </Link>
  );
}

/*
Email body may contain email addresses that are enclosed by angle brackets (e.g. <test.queue@amazon.com>). When rendering on the DOM, these email addresses are lost
as they are displayed as custom HTML elements (e.g. <test.queue@amazon.com></test.queue@amazon.com>). The angle brackets must be escaped as their HTML character
code so the entire email address is properly rendered as a string on the DOM.
*/
function retainEmailAddressEnclosedByHTMLTags(emailBody: string): string {
  // if email body does not contain any email addresses that are surrounded by angle brackets, do nothing
  if (!pattern.test(emailBody)) {
    return emailBody;
  }

  // find every occurence of the pattern and replace its first occurence of < and > with their HTML character code
  emailBody = emailBody.replace(pattern, str =>
    encodeFirstAndLastAngleBrackets(str)
  );

  return emailBody;
}

function encodeFirstAndLastAngleBrackets(text: string): string {
  return text.replace('<', '&lt;').replace('>', '&gt;');
}
